import React, { useEffect, useState } from "react";
import {
  Container,
  Grid,
  Paper,
  Tabs,
  Tab,
  Typography,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import gfm from "remark-gfm"; // Optional: Enables GitHub Flavored Markdown

// Import markdown file contents for About, Privacy, and Terms.
import termsOfServiceContent from "./terms_of_service.md";
import privacyPolicyContent from "./privacy_policy.md";
import aboutContent from "./about.md";

// Import your FAQ with guide component – ensure this is a React component, not a markdown file.
import FaqWithGuide from "./faqWithGuide";

// Mapping the routes to file paths/components (for FAQ, we'll render the component)
const markdownFiles = {
  "/about": aboutContent,
  "/privacy": privacyPolicyContent,
  "/terms": termsOfServiceContent,
  // For FAQ, we store a special marker (or the component) but skip fetching.
  "/faq": null,
};

const PrivacyTermsAbout = () => {
  const location = useLocation();
  const [markdownContent, setMarkdownContent] = useState("");
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [selectedTab, setSelectedTab] = useState(0);

  // When the location changes, update the tab and fetch content if needed.
  useEffect(() => {
    const pathKeys = Object.keys(markdownFiles);
    const tabIndex = pathKeys.indexOf(location.pathname);
    setSelectedTab(tabIndex >= 0 ? tabIndex : 0); // Default to "About" if path doesn't match
    fetchMarkdownFile(location.pathname);
  }, [location.pathname]);

  // Fetch the markdown file only if the current path is not "/faq"
  const fetchMarkdownFile = (filePath) => {
    if (filePath === "/faq") {
      setMarkdownContent("");
      return;
    }
    const markdownFilePath = markdownFiles[filePath];
    if (markdownFilePath) {
      fetch(markdownFilePath)
        .then((response) => {
          if (!response.ok) {
            throw new Error("Failed to fetch the markdown file");
          }
          return response.text();
        })
        .then((text) => setMarkdownContent(text))
        .catch((error) =>
          console.error("Error fetching markdown file:", error)
        );
    } else {
      setMarkdownContent("Content not found.");
    }
  };

  // Update content when the selected tab changes (if not FAQ)
  useEffect(() => {
    const path = Object.keys(markdownFiles)[selectedTab];
    if (path && path !== "/faq") {
      fetchMarkdownFile(path);
    }
  }, [selectedTab]);

  const getTabBackgroundColor = (index) =>
    selectedTab === index ? "rgba(0, 0, 0, 0.1)" : "transparent";

  return (
    <Container maxWidth="lg">
      <Grid container spacing={3}>
        {!isSmallScreen ? (
          <Grid item xs={3}>
            <Paper>
              <Tabs
                orientation="vertical"
                variant="scrollable"
                value={selectedTab}
                onChange={(event, newValue) => setSelectedTab(newValue)}
              >
                {Object.keys(markdownFiles).map((path, index) => (
                  <Tab
                    key={index}
                    label={
                      path === "/about"
                        ? "About"
                        : path === "/privacy"
                        ? "Privacy Policy"
                        : path === "/terms"
                        ? "Terms of Service"
                        : "FAQ"
                    }
                    sx={{
                      backgroundColor: getTabBackgroundColor(index),
                    }}
                  />
                ))}
              </Tabs>
            </Paper>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <Paper sx={{ padding: 3, marginBottom: 3 }}>
              <Select
                fullWidth
                value={selectedTab}
                onChange={(event) => setSelectedTab(event.target.value)}
              >
                <MenuItem value={0}>About</MenuItem>
                <MenuItem value={1}>Privacy Policy</MenuItem>
                <MenuItem value={2}>Terms of Service</MenuItem>
                <MenuItem value={3}>FAQ</MenuItem>
              </Select>
            </Paper>
          </Grid>
        )}
        <Grid item xs={12} md={isSmallScreen ? 12 : 9}>
          <Paper sx={{ padding: 3 }}>
            <Typography variant="body1" align="justify" sx={{ margin: 3 }}>
              {selectedTab === 3 ? (
                // Render FAQ component for FAQ tab
                <FaqWithGuide />
              ) : (
                markdownContent && (
                  <ReactMarkdown
                    remarkPlugins={[gfm]}
                    children={markdownContent}
                  />
                )
              )}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default PrivacyTermsAbout;
